import React from "react";

const exports = {
  messages: {
    // Player messages
    "Processing app failures": (
      <span>
        The player will periodically check for app failures. This message
        indicates that the player has completed a check cycle. When there is an
        app failure it will be reported in the <u>failures</u> section.
        <br />
        <b>Expect</b> these messages regularly with empty failures list.
        <br />
        <b>Let us know</b> if you see non-empty failures often.
      </span>
    ),
    "Window stats": (
      <span>
        Internal window manager stats. <u>stats</u> will be empty most of the
        time. It is normal to see an <u>oom</u> counter in the <u>stats</u>
        field. The counter is the number of times an app encountered an out of
        memory situation. Note that, the player&apos;s internal OOM is different
        than the operating system&apos;s OOM. The player can handle internal OOM
        situations gracefully.
        <br />
        <b>Expect</b> these messages regularly with or without <u>oom</u>{" "}
        fields.
        <br />
        <b>Let us know</b> if you see the <u>oom</u> counter is increasing every
        few minutes.
      </span>
    ),
    "Request interceptor stats": (
      <span>
        Internal stats about outgoing calls made by the apps.
        <br />
        <b>Expect</b> these messages regularly.
      </span>
    ),
    // Health checks
    "Health check cycle is starting.": (
      <span>
        The player will periodically perform app health checks. This message
        indicates that the player is about to start a new health check cycle.
        <br />
        <b>Expect</b> these messages regularly.
      </span>
    ),
    "Application is healthy.": (
      <span>
        The player will periodically perform app health checks. This message
        indicates that the player performed the health check and found out that
        an app was healthy.
        <br />
        <b>Expect</b> one of these message per app during a health check cycle.
      </span>
    ),
    "Application failed to pass the health check.": (
      <span>
        The player will periodically perform app health checks. This message
        indicates that the player performed the health check and found out that
        an app was <u>unhealthy</u>.
        <br />
        <b>Let us know</b> if you see these messages frequently. This might
        indicate a player / app bug.
      </span>
    ),
    // Caching
    "AutoCache stats.": (
      <span>
        Internal disk caching stats.
        <br />
        <b>Expect</b> these messages regularly.
      </span>
    ),
    // Messaging
    "Zombie message cleaner": (
      <span>
        Internal messaging stats.
        <br />
        <b>Expect</b> these messages regularly.
      </span>
    ),

    // Scheduler messages
    "View finished": (
      <span>
        The player finished displaying a view. View details are provided in the
        extra section.
      </span>
    ),

    // Ads app messages
    "Ad server returned no ads.": (
      <span>
        The ads app logs this message when the ad server doesn&apos;t return any
        ads. This message indicates that the player can reach out to the ad
        server. However, the ad server is not returning ads for some reason
        (budget problem, campaign setup problems, etc.).
        <br />
        <b>Expect</b> to see these messages from time to time. Depending on your
        setup, the ad server may not have ads to return.
        <br />
        <b>Check</b> your campaign setup if you see these messages when the
        player is supposed to show ads.
      </span>
    ),
    "Ads app stats": (
      <span>
        Ads app periodically logs some stats about its internal operation. These
        messages are intended for the ads app developers.
      </span>
    ),
    "Sample ad request": (
      <span>
        Ads app periodically logs ad requests for debugging purposes. The JSON
        message printed is the actual request payload the app sent to the ad
        server.
      </span>
    ),
    "DOMRegistry: Finished gc cycle.": (
      <span>
        This message will get log on ads app garbage collection activity.
      </span>
    ),
    "Rejecting ad. Ad failed the competitive separation check.": (
      <span>
        The ads app logs this message when it rejects an ad due to competitive
        separation restriction.
        <br />
        <b>Expect</b> to see these messages from time to time. It is normal for
        the ads app to reject ads this way.
      </span>
    ),
    "Rejecting ad. Failed to cache the ad asset.": (
      <span>
        The ads app logs this message when it gets a valid ad from the ad server
        but fails to prepare the final view because of an issue with disk
        caching.
        <br />
        <b>Check</b> internet connection and disk usage of the player. It is
        <u>not</u> normal to get these messages.
        <br />
        <b>Let us know</b> if you see these messages frequently.
      </span>
    ),
    "Ad server request failed.": (
      <span>
        The ads app logs this message when an ad request fails. It is not normal
        to see these messages. Either there is a problem with the internet
        connection or the server is returning an error.
        <br />
        <b>Check</b> the internet connection.
        <b>Let us know</b> if you think internet connection is okay. It might be
        a problem with the ad server.
      </span>
    ),
    "Rejecting ad. Image asset is too large.": (
      <span>
        The ads app logs this message when it receives an image ad that exceeds
        the allowed file size limit. The details section will provide more
        information about the file size limit and the file size of the asset
        rejected.
        <br />
        <b>Check</b> your campaign setup and make sure to replace the large
        asset.
      </span>
    ),
    "Rejecting ad. Failed to check image asset size.": (
      <span>
        The ads app logs this message when it cannot check the size of the image
        ad. The ads app rejects these assets to be safe. Generally, these
        messages mean the asset server <u>is not</u> returning the
        &apos;Content-Length&apos; header.
        <br />
        <b>Let us know</b> if you see these messages. It may be an issue with
        the ad server.
      </span>
    ),
    "Non-video ad finished.": (
      <span>
        A non-video ad finished. It is normal to see these messages regularly.
      </span>
    ),
    "Video ad finished.": (
      <span>
        A video ad finished. It is normal to see these messages regularly.
      </span>
    ),
  },
};

const AD_REQUESTS_ARE_FAILING =
  "Ad requests are failing. Reporting this " +
  "incident on dashboard. The player will not restart the application.";

exports[AD_REQUESTS_ARE_FAILING] = (
  <span>
    The ads app logs this message when ad requests fail too frequently. The
    following conditions are considered a failure:
    <ul>
      <li>
        Ad requests are failing for some reason (e.g. internet is down, ad
        server is down, etc.)
      </li>
      <li>
        Ad requests are not failing but the ad server is not returning any valid
        ads.
      </li>
    </ul>
    Whenever the ads app logs one of these messages, a warning event will be
    raised on Fleet.
    <br />
    <b>Check</b> device health and your campaign setup if you see these messages
    when the player is supposed to show ads.
  </span>
);

exports.APP_FAILURE_DETECTED = "Failures detected";
exports.APP_FAILURE_MESSAGES = [
  "Processing app failures",
  "Checking app failures",
];
exports.APP_FAILURE_NOT_DETECTED = "No failures detected";
exports.MIN_VER_FOR_EXTENDED_LOGS = "3.0.2";

module.exports = exports;
